import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Alert,
  TextField
} from '@mui/material';
import { Add as AddIcon, Remove as RemoveIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';
import { useBuild } from '../contexts/BuildContext';

const TagsSection = ({ onUpdate, boostInput = false, fieldName = 'tags' }) => {
  const { fetchCachedList, selectedIndex } = useBuild();
  const [currentTag, setCurrentTag] = useState('');
  const [boost, setBoost] = useState(1);
  const [tagOptions, setTagOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch tag options on component mount and when selectedIndex changes
  useEffect(() => {
    const fetchTagOptions = async () => {
      try {
        setLoading(true);
        setError(null);
        const endpoint = `/search/frequent_values/${selectedIndex}/tags`;
        const tagsData = await fetchCachedList(endpoint);
        setTagOptions(tagsData);
      } catch (err) {
        console.error('Failed to fetch tag options:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTagOptions();
  }, [fetchCachedList, selectedIndex]);

  const handleTagAction = (actionType) => {
    if (!currentTag) return;
    
    const updateData = {
      action: actionType,
      filter: 'terms',
      field: fieldName,
      value: [currentTag]
    };
    
    // Add boost value if boostInput is enabled
    if (boostInput) {
      updateData.boost = boost;
    }
    
    onUpdate(updateData);
    setCurrentTag(''); // Reset after adding
  };

  // Show loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error">
            Failed to load tags: {error}
          </Alert>
        </Box>
      )}
      {error ? (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Tag"
          value={currentTag}
          onChange={(e) => setCurrentTag(e.target.value)}
          placeholder="Enter tag manually"
          disabled={loading}
        />
      ) : (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Tag</InputLabel>
          <Select
            value={currentTag}
            label="Tag"
            onChange={(e) => setCurrentTag(e.target.value)}
            disabled={loading}
          >
            {tagOptions.map((tag) => (
              <MenuItem key={tag.id} value={tag.id}>
                {tag.id} ({tag.count})
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      
      {boostInput && (
        <TextField
          fullWidth
          sx={{ mb: 2 }}
          label="Boost"
          type="number"
          value={boost}
          onChange={(e) => setBoost(parseFloat(e.target.value) || 1)}
          disabled={loading}
        />
      )}
      
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleTagAction('include')}
          disabled={!currentTag || loading}
        >
          <AddIcon />
        </Button>
        
        {boostInput && (
          <Button
            variant="contained"
            color="success"
            onClick={() => handleTagAction('boost')}
            disabled={!currentTag || loading}
          >
            <TrendingUpIcon />
          </Button>
        )}
        
        <Button
          variant="contained"
          color="error"
          onClick={() => handleTagAction('exclude')}
          disabled={!currentTag || loading}
        >
          <RemoveIcon />
        </Button>
      </Box>
    </Box>
  );
};

export default TagsSection;
